import type { LfgLftPostListParams } from "@/shared/api/lfg-lft-types";

/**
 * Фильтры доски LFG/LFT (KAN-1351): одно состояние, одна запись в URL, один перевод
 * в параметры API. Значения — те же имена, что принимает бэкенд (KAN-1349/1350).
 */

export const BOARD_SEGMENTS = ["all", "players", "teams"] as const;
export type BoardSegment = (typeof BOARD_SEGMENTS)[number];

/** «Ищут команду» — объявления игроков (Lfg), «Ищут игроков» — объявления команд (Lft). */
const SEGMENT_TYPE: Record<BoardSegment, string | null> = { all: null, players: "Lfg", teams: "Lft" };

export const LFG_ROLES = ["Top", "Jungle", "Mid", "Adc", "Support", "Autofill", "Substitute", "Coach"] as const;
export type LfgRole = (typeof LFG_ROLES)[number];

/** Индекс = тир бэкенда (LfgRankTier): 0 — Анранкед … 10 — Претендент. */
export const LFG_RANKS = [
  "Unranked", "Iron", "Bronze", "Silver", "Gold", "Platinum",
  "Emerald", "Diamond", "Master", "Grandmaster", "Challenger",
] as const;
export type LfgRank = (typeof LFG_RANKS)[number];

/** Ранг из Riot («GOLD II», «Unranked», null) → ключ шкалы; мусор и пусто — Анранкед. */
export function toLfgRank(raw: string | null | undefined): LfgRank {
  const head = (raw ?? "").trim().split(/[ _]/)[0]?.toLowerCase() ?? "";
  return LFG_RANKS.find((r) => r.toLowerCase() === head) ?? "Unranked";
}

export function isLfgRole(v: string): v is LfgRole {
  return (LFG_ROLES as readonly string[]).includes(v);
}

export const LFG_MIC = ["any", "with", "without"] as const;
export type LfgMicFilter = (typeof LFG_MIC)[number];
const MIC_API: Record<LfgMicFilter, string | null> = { any: null, with: "With", without: "Without" };

export const LFG_FORMATS = ["SoloDuo", "Flex", "Clash", "Custom", "Scrim", "Tournament", "Aram", "Arena"] as const;
export type LfgFormat = (typeof LFG_FORMATS)[number];

/** RiotRegion без PBE и SEA (решение 25.09.2026) — ровно то, что принимает бэкенд. */
export const LFG_SERVERS = [
  "EUW1", "EUN1", "RU", "TR1", "ME1", "NA1", "BR1", "LA1", "LA2", "KR", "JP1", "OC1", "SG2", "TW2", "VN2",
] as const;
export type LfgServer = (typeof LFG_SERVERS)[number];

export const LFG_LANGUAGES = ["Ru", "En"] as const;
export type LfgLanguage = (typeof LFG_LANGUAGES)[number];

export const LFG_SORTS = ["new", "rank", "mic"] as const;
export type LfgSort = (typeof LFG_SORTS)[number];

/** Восемь слотов по 3 часа в поясе зрителя: слот i — часы [3i, 3i+3). */
export const HOUR_SLOT_COUNT = 8;

export interface RankRange {
  min: number;
  max: number;
}

export interface BoardFilters {
  segment: BoardSegment;
  q: string;
  roles: LfgRole[];
  rank: RankRange | null;
  mic: LfgMicFilter;
  hourSlots: number[];
  formats: LfgFormat[];
  servers: LfgServer[];
  languages: LfgLanguage[];
  sort: LfgSort;
  page: number;
}

export const EMPTY_FILTERS: BoardFilters = {
  segment: "all",
  q: "",
  roles: [],
  rank: null,
  mic: "any",
  hourSlots: [],
  formats: [],
  servers: [],
  languages: [],
  sort: "new",
  page: 1,
};

function pickOne<T extends string>(all: readonly T[], raw: string | null, fallback: T): T {
  return all.find((v) => v === raw) ?? fallback;
}

/** Порядок — как в списке констант, а не как кликали: URL и пилюли не прыгают. */
function pickMany<T extends string>(all: readonly T[], raw: string | null): T[] {
  const set = new Set((raw ?? "").split(",").filter(Boolean));
  return all.filter((v) => set.has(v));
}

function parseRank(raw: string | null): RankRange | null {
  const m = /^(\d+)-(\d+)$/.exec(raw ?? "");
  if (!m) return null;
  const min = Number(m[1]);
  const max = Number(m[2]);
  return min <= max && max < LFG_RANKS.length ? { min, max } : null;
}

export function parseBoardFilters(sp: URLSearchParams): BoardFilters {
  const slots = new Set((sp.get("hours") ?? "").split(",").filter((s) => /^\d$/.test(s)).map(Number));
  const page = Number(sp.get("page"));
  return {
    segment: pickOne(BOARD_SEGMENTS, sp.get("seg"), "all"),
    q: sp.get("q")?.trim() ?? "",
    roles: pickMany(LFG_ROLES, sp.get("roles")),
    rank: parseRank(sp.get("rank")),
    mic: pickOne(LFG_MIC, sp.get("mic"), "any"),
    hourSlots: [...Array(HOUR_SLOT_COUNT).keys()].filter((i) => slots.has(i)),
    formats: pickMany(LFG_FORMATS, sp.get("formats")),
    servers: pickMany(LFG_SERVERS, sp.get("servers")),
    languages: pickMany(LFG_LANGUAGES, sp.get("lang")),
    sort: pickOne(LFG_SORTS, sp.get("sort"), "new"),
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

/** Только отличия от пустых фильтров — ссылка на доску без фильтров остаётся чистой. */
export function serializeBoardFilters(f: BoardFilters): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.segment !== "all") sp.set("seg", f.segment);
  if (f.q) sp.set("q", f.q);
  if (f.roles.length) sp.set("roles", f.roles.join(","));
  if (f.rank) sp.set("rank", `${f.rank.min}-${f.rank.max}`);
  if (f.mic !== "any") sp.set("mic", f.mic);
  if (f.hourSlots.length) sp.set("hours", f.hourSlots.join(","));
  if (f.formats.length) sp.set("formats", f.formats.join(","));
  if (f.servers.length) sp.set("servers", f.servers.join(","));
  if (f.languages.length) sp.set("lang", f.languages.join(","));
  if (f.sort !== "new") sp.set("sort", f.sort);
  if (f.page > 1) sp.set("page", String(f.page));
  return sp;
}

/**
 * Слоты пояса зрителя → маска часов UTC (бит N — час N по UTC).
 * `tzOffsetMin` — как `Date#getTimezoneOffset()`: UTC минус местное, для МСК −180.
 */
export function slotsToUtcMask(slots: readonly number[], tzOffsetMin: number): number {
  let mask = 0;
  for (const slot of slots)
    for (let h = slot * 3; h < slot * 3 + 3; h++) {
      const utcMin = (((h * 60 + tzOffsetMin) % 1440) + 1440) % 1440;
      mask |= 1 << Math.floor(utcMin / 60);
    }
  return mask;
}

export function toListParams(f: BoardFilters, tzOffsetMin: number, pageSize: number): LfgLftPostListParams {
  return {
    type: SEGMENT_TYPE[f.segment],
    q: f.q || null,
    roles: f.roles,
    minRank: f.rank ? LFG_RANKS[f.rank.min] : null,
    maxRank: f.rank ? LFG_RANKS[f.rank.max] : null,
    mic: MIC_API[f.mic],
    hoursUtcMask: f.hourSlots.length ? slotsToUtcMask(f.hourSlots, tzOffsetMin) : null,
    servers: f.servers,
    searchPurposes: f.formats,
    languages: f.languages,
    sort: f.sort,
    page: f.page,
    pageSize,
  };
}

/**
 * Ранг выбирается диапазоном (решение 28.09.2026): первый клик — один тир, клик за краем
 * расширяет, клик по краю его снимает, клик внутри — начинает заново с этого тира.
 */
export function toggleRank(range: RankRange | null, tier: number): RankRange | null {
  if (!range) return { min: tier, max: tier };
  if (tier < range.min) return { min: tier, max: range.max };
  if (tier > range.max) return { min: range.min, max: tier };
  if (range.min === range.max) return null;
  if (tier === range.min) return { min: tier + 1, max: range.max };
  if (tier === range.max) return { min: range.min, max: tier - 1 };
  return { min: tier, max: tier };
}

export function toggleIn<T>(list: readonly T[], value: T, order: readonly T[]): T[] {
  const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  return order.filter((v) => next.includes(v));
}

/** Соседние слоты склеиваются: [6, 7] → «18–24». Все восемь — null (подпись «Весь день»). */
export function mergeHourSlots(slots: readonly number[]): Array<[from: number, to: number]> | null {
  if (slots.length === HOUR_SLOT_COUNT) return null;
  const ranges: Array<[number, number]> = [];
  for (const s of [...slots].sort((a, b) => a - b)) {
    const last = ranges[ranges.length - 1];
    if (last && last[1] === s * 3) last[1] = s * 3 + 3;
    else ranges.push([s * 3, s * 3 + 3]);
  }
  return ranges;
}

/**
 * Маска часов UTC → диапазоны в поясе зрителя: [[18, 24]], через полночь — [[21, 3]].
 * Все 24 часа — null (подпись «Весь день»); пустая маска — [] («не указано»).
 */
export function utcMaskToLocalRanges(mask: number, tzOffsetMin: number): Array<[from: number, to: number]> | null {
  const local = [...Array(24).keys()].map((h) => {
    const utc = Math.floor(((((h * 60 + tzOffsetMin) % 1440) + 1440) % 1440) / 60);
    return (mask & (1 << utc)) !== 0;
  });
  if (local.every(Boolean)) return null;
  // Старт с пустого часа: так диапазон через полночь не рвётся на два.
  const start = local.indexOf(false);
  const ranges: Array<[number, number]> = [];
  for (let i = 1; i <= 24; i++) {
    const h = (start + i) % 24;
    if (!local[h]) continue;
    const last = ranges[ranges.length - 1];
    if (last && last[1] % 24 === h) last[1] = (h + 1) % 24 || 24;
    else ranges.push([h, (h + 1) % 24 || 24]);
  }
  return ranges;
}

export function countActiveFilters(f: BoardFilters): number {
  return (
    f.roles.length + (f.rank ? 1 : 0) + (f.mic !== "any" ? 1 : 0) + f.hourSlots.length +
    f.formats.length + f.servers.length + f.languages.length
  );
}

/** Смена фильтра возвращает на первую страницу: иначе новый набор открывается на пустой странице 5. */
export function withFilters(f: BoardFilters, patch: Partial<Omit<BoardFilters, "page">>): BoardFilters {
  return { ...f, ...patch, page: 1 };
}
